import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useFacility } from "@/context/FacilityContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PatientPicker } from "@/components/opd/PatientPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, BedDouble } from "lucide-react";
import { toast } from "sonner";

export default function IpdAdmit() {
  const { activeId } = useFacility();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ doctors: [], departments: [] });
  const [beds, setBeds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ patient: null, department: "", bed_id: "", doctor_id: "", reason: "", notes: "" });

  const load = useCallback(() => {
    if (!activeId) return;
    api.get(`/ipd/meta?facility_id=${activeId}`).then((r) => setMeta(r.data)).catch(() => {});
    api.get(`/ipd/beds?facility_id=${activeId}&status=available`).then((r) => setBeds(r.data)).catch(() => setBeds([]));
  }, [activeId]);
  useEffect(load, [load]);

  const bedsForDept = beds.filter((b) => !form.department || b.department === form.department);

  const admit = async () => {
    if (!form.patient) { toast.error("Select or register a patient"); return; }
    if (!form.bed_id) { toast.error("Select an available bed"); return; }
    setSaving(true);
    try {
      const doc = meta.doctors.find((d) => d.id === form.doctor_id);
      const bed = beds.find((b) => b.id === form.bed_id);
      const { data } = await api.post("/ipd/admissions", {
        facility_id: activeId, patient_id: form.patient.id, bed_id: form.bed_id,
        department: form.department || bed?.department, admitting_doctor_id: form.doctor_id || null,
        admitting_doctor_name: doc?.name || null, reason: form.reason, notes: form.notes });
      toast.success(`Admitted ${form.patient.name} · ${data.ipd_no}`);
      navigate("/app/ipd/admissions");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="ipd-admit-page">
      <PageHeader title="Admit Patient" subtitle="Admit a new or existing patient into an available bed." />

      <div className="max-w-2xl bg-white border border-slate-200 rounded-md p-6 space-y-5">
        <div className="space-y-1.5">
          <Label className="text-xs">Patient</Label>
          <PatientPicker value={form.patient} onChange={(p) => setForm({ ...form, patient: p })} testId="admit-patient-picker" />
          <p className="text-[11px] text-slate-400">Search an existing patient or click "New" to register on the spot.</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label className="text-xs">Department</Label>
            <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value, bed_id: "" })} data-testid="admit-department-select">
              <option value="">All departments</option>
              {meta.departments.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Bed</Label>
            <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.bed_id} onChange={(e) => setForm({ ...form, bed_id: e.target.value })} data-testid="admit-bed-select">
              <option value="">{bedsForDept.length ? "Select an available bed…" : "No available beds"}</option>
              {bedsForDept.map((b) => <option key={b.id} value={b.id}>{b.bed_number} · {b.ward || b.department} · {b.bed_type}</option>)}
            </select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Admitting doctor</Label>
            <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.doctor_id} onChange={(e) => setForm({ ...form, doctor_id: e.target.value })} data-testid="admit-doctor-select">
              <option value="">Unassigned</option>
              {meta.doctors.map((d) => <option key={d.id} value={d.id}>{d.name}{d.designation ? ` — ${d.designation}` : ""}</option>)}
            </select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Reason for admission</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Acute chest pain" data-testid="admit-reason-input" /></div>
        </div>

        <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <Button variant="outline" onClick={() => navigate("/app/ipd/admissions")}>Cancel</Button>
          <Button onClick={admit} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="admit-submit-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><BedDouble className="h-4 w-4 mr-1.5" /> Admit Patient</>}</Button>
        </div>
      </div>
    </div>
  );
}
